import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  Input,
  SecurityContext,
  signal,
  inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgfFormControl } from '@ng-falcon/ngf-form/+model/ngf-form-control';
import { Subject, takeUntil } from 'rxjs';
import { DialogService } from '../../dialog/dialog.service';
import { TextEditorTemplate } from '@ng-falcon/+legacy/text-editor/text-editor-templates/+model/text-editor-template.model';

// statically register our extensions for later use by config
// this is where we should register any extensions we create
import Quill from 'quill';
import { NgfRichTextImageExtension, NgfRichTextTemplateExtension } from '../extensions';
import { EmbeddedImageService } from '../extensions/embedded-images/embedded-image.service';
Quill.register('modules/template', NgfRichTextTemplateExtension);
Quill.register('modules/image', NgfRichTextImageExtension);

/** A Quill toolbar container: groups of buttons, each a format name or a `{ format: value }` object. */
export type NgfRichTextToolbarContainer = ReadonlyArray<ReadonlyArray<string | Record<string, unknown>>>;

const DEFAULT_TOOLBAR_CONTAINER: NgfRichTextToolbarContainer = [
  [{ 'header': [false, 1, 2, 3] }],
  ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
  [{ 'background': [false, 'yellow'] }],
  ['code-block', 'code'],
  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
  [{ 'indent': '-1' }, { 'indent': '+1' }],
];

@Component({
    selector: 'ngf-rich-text-editor',
    templateUrl: './ngf-rich-text-editor.component.html',
    styleUrl: './ngf-rich-text-editor.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NgfRichTextEditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class NgfRichTextEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {

  @Input() placeholder: string;

  @Input() ariaLabelledby?: string;

  @Input() templates: readonly TextEditorTemplate[] = [];

  @Input() allowEmbeddedImages: boolean = false;

  @Input()
  showCounter: boolean = false;

  @Input()
  maxLength: number;

  /**
   * Toolbar button groups. Defaults to the full toolbar. Read once at initialization; the array is
   * copied, so the caller's array is never mutated.
   */
  @Input() toolbar: NgfRichTextToolbarContainer | undefined;

  /**
   * Quill `formats` whitelist (toolbar-independent: also limits e.g. pasted content). When unset,
   * every registered format is allowed. Read once at initialization.
   */
  @Input() formats: string[] | undefined;

  // services needed for our extensions; 
  // we inject them here and pass them down via 
  // the modules config object when initializing the editor
  private dialogService = inject(DialogService);
  private embeddedImageService = inject(EmbeddedImageService, { optional: true });


  private sanitizer = inject(DomSanitizer);

  readonly control = new NgfFormControl<string>(null);

  ready$ = signal(false)

  /**
   * Quill `modules` config. Built fresh in `ngOnInit` (after inputs are set) rather than mutated
   * in place, so the object handed to `quill-editor` never changes after initialization.
   */
  // TODO: add strong typed interface for this
  modules: Record<string, any> = {};

  private onChange = (value: string) => { };
  private onTouched = () => { };
  private destroy$ = new Subject<void>();

  /**
   * Guards against Quill 2's `MutationObserver`-driven DOM normalization (e.g. rebuilding
   * `code-block-container` structure) firing an async `text-change` with `source: 'user'`
   * after `writeValue` loads content. Starts `true` since `writeValue` runs before the
   * `<quill-editor>` view even exists on first load; `onEditorCreated` is the reliable
   * point where that initial correction (a microtask) is guaranteed to have resolved.
   */
  private suppressChangeOnLoad = true;

  // Set once the underlying Quill instance exists (see `onEditorCreated`). Distinguishes
  // the first load, where `writeValue` runs before the editor view exists, from later
  // `writeValue` calls, where Quill already exists and reacts synchronously.
  private editorReady = false;

  readonly counter = signal<null | number>(null);

  ngOnInit(): void {

    if (this.showCounter) {
      this.counter.set(this.control.value?.length ?? 0);

      if (this.maxLength && !this.control.hasValidator(Validators.maxLength(this.maxLength))) {
          console.warn(`NgfRichTextEditorComponent: maxLength validator missing from control, or does not match maxLength of ${this.maxLength}.`); 
      }
    }

    // Subscribe to form control value changes and propagate to parent form
    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!this.suppressChangeOnLoad) {
          this.onChange(value);
        }

        if (this.showCounter) {
          this.counter.set(value ? value.length : 0);
        }
      });

    this.modules = this.buildModules();
    this.formats = this.formats ? [...this.formats] : undefined;

    // only bring `quill-editor` to DOM after initialization is done
    this.ready$.set(true);
  }

  private buildModules(): Record<string, any> {
    const container: any[] = (this.toolbar ?? DEFAULT_TOOLBAR_CONTAINER)
      .map(group => [...group]);

    // required for any custom handlers we add later
    // we can populate with functions scoped this this component
    // or provide an empty function if our extension adds a handler internally
    // e.g. `template` handler for `button.ql-template` is `() => {}` because
    // the extension handles opening the dialog internally
    const handlers: Record<string, () => void> = {};

    const modules: Record<string, any> = {
      // required for template extension
      // only populate if templates are provided
      template: {},
      // required for image extension
      // only populate if allowEmbeddedImages is true
      image: {},
      toolbar: { container, handlers }
    };

    // if templates are provided, configure the template module
    if (this.templates && this.templates.length > 0) {
      modules['template'] = {
        control: this.control,
        templates: this.templates,
        dialogService: this.dialogService
      };

      // add toolbar button to rendered toolbar
      container.push([ {'template': 'use template'} ]);
      // add empty handler; this is needed, or else
      // the handler added via `NgfRichTextTemplateExtension` won't be called
      handlers['template'] = () => {};
    }

    if (this.allowEmbeddedImages) {
      if (!this.embeddedImageService) {
        throw new Error('NgfRichTextEditorComponent: embeddedImageService is required when allowEmbeddedImages is true. Please provide EmbeddedImageService via Angular DI to the host component.');
      }

      modules['image'] = {
        embeddedImageService: this.embeddedImageService,
        onUnsupportedFileError: (count: number, supportedFormats: string) => {
          this.dialogService.openErrorDialog({
            title: 'Unsupported file type',
            message: `${count} file(s) could not be uploaded. Only ${supportedFormats} image formats are supported.`
          });
        }
      };
      // add toolbar button to rendered toolbar
      container.push([ {'image': 'add image'} ]);
      // add empty handler; this is needed, or else
      // the handler added via `NgfRichTextImageExtension` won't be called
      handlers['image'] = () => {};
    }

    return modules;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string): void {
    this.suppressChangeOnLoad = true;
    this.control.setValue(this.sanitizeButPreserveHighlights(value), { emitEvent: false });

    if (this.editorReady) {
      // The editor already exists, so the `setContents` call above runs synchronously
      // and any corrective mutation resolves (as a microtask) well within this macrotask.
      // On first load there's no editor yet to react synchronously -- `onEditorCreated`
      // lifts the guard for that case instead.
      setTimeout(() => { this.suppressChangeOnLoad = false; });
    }
  }

  /**
   * 
   * @param value string with html content
   * @returns html content -- sanitized to prevent xss issues
   * Note: the DomSanitizer by default strips inline styles, but the rich text editor generates them as
   * part of supporting highlighted text; Additionally, for whatever reason Quill getSemanticHtml (output)
   * turns it's internal .ql-bg-yellow class into an inline style, but does not convert it back when editing it (input).
   * 
   * To workaround these two incompatible issues, we first convert any inline highlights (We only support 'yellow' / #FFFF00) into the .ql-bg-yellow class, 
   * then run the sanitizer, then convert those classes back into inline styles so that they can be rendered properly in the editor.
   */
  private sanitizeButPreserveHighlights(value: string): string {
    const classHighlightedText = value ? value.replace(/style="background-color: ?(yellow|#FFFF00);?"/gi, 'class="ql-bg-yellow"') : value;
    const sanitizedValue = classHighlightedText ? this.sanitizer.sanitize(SecurityContext.HTML, classHighlightedText) : classHighlightedText;
    const styleHighlightedText = sanitizedValue ? sanitizedValue.replace(/class="ql-bg-yellow"/gi, 'style="background-color: yellow;"') : sanitizedValue;
    
    return styleHighlightedText;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  onEditorBlur(): void {
    this.onTouched();
  }

  onEditorCreated(quill: any): void {
    /**
     * Workaround for Quill 2.0.3 Known issue where it converts multiple consecutive spaces into `&nbsp;`
     * We've worked around this in the past by adding an `nbspToSpace` pipe on the viewer side, but this
     * approach is a little cleaner since it cuts off the undesired behavior before being processed elsewhere.
     */
    quill.legacyGetSemanticHTML = quill.getSemanticHTML; // capture original function

    // overwrite `getSemanticHTML` to replace `&nbsp;` with regular spaces, while preserving multiple consecutive spaces
    quill.getSemanticHTML = (a: number, b: number) => quill.legacyGetSemanticHTML(a, b)
      .replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ');

    if (this.ariaLabelledby) {
      const qlEditor = quill.container.querySelector('.ql-editor') as HTMLElement | null;
      if (qlEditor) {
        qlEditor.setAttribute('aria-labelledby', this.ariaLabelledby);
        qlEditor.setAttribute('role', 'textbox');
        qlEditor.setAttribute('aria-multiline', 'true');
      }
    }

    this.editorReady = true;

    // Deferred by a task, same margin as later `writeValue` calls -- `onEditorCreated`
    // is itself rAF-scheduled, so it isn't a reliable enough signal on its own that
    // Quill's corrective mutation for the initial load has resolved.
    setTimeout(() => { this.suppressChangeOnLoad = false; });
  }

}
